import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ListingCard from '../components/ListingCard';
import CATEGORIES from '../constants/categories';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';

const LIMIT = 20;

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const totalPages = Math.ceil(total / LIMIT);

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
  }, [searchParams]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (category) params.category = category;
      if (search) params.search = search;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/listings`, { params });
      setListings(res.data.listings);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchListings(); }, [searchParams]);

  const goToPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applySearch = () => {
    const next = new URLSearchParams(searchParams);
    const q = searchInput.trim();
    if (q) next.set('search', q); else next.delete('search');
    next.set('page', '1');
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const hasFilters = category || search;

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Page Header */}
        <PageHeader title="Browse Services" subtitle="Find trusted cleaning companies near you" />

        {/* Search & Filter */}
        <form
          onSubmit={e => { e.preventDefault(); applySearch(); }}
          className="flex flex-col sm:flex-row gap-3 mb-8"
        >
          <div className="relative flex-1">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applySearch()}
              placeholder="Search services…"
              aria-label="Search services"
              className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 text-sm transition-all duration-200"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  const next = new URLSearchParams(searchParams);
                  next.delete('search');
                  next.set('page', '1');
                  setSearchParams(next);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-500 transition-colors duration-200 text-base leading-none"
              >
                ×
              </button>
            )}
          </div>

          <select
            value={category}
            aria-label="Filter by category"
            onChange={e => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set('category', e.target.value); else next.delete('category');
              next.set('page', '1');
              setSearchParams(next);
            }}
            className="px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <Button type="submit" variant="primary">Search</Button>

          {hasFilters && (
            <Button type="button" variant="secondary" onClick={clearFilters}>Clear</Button>
          )}
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="flex flex-col items-center py-24 gap-3 text-center">
            <p className="font-heading text-lg font-semibold text-ink-900">No services found</p>
            <p className="text-sm text-ink-400">
              Try a different search term or category
              {hasFilters && (
                <>, or{' '}
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-terra-400 hover:text-terra-500 underline underline-offset-2 transition-colors duration-200"
                  >
                    clear all filters
                  </button>
                </>
              )}.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-ink-400 mb-4">
              {total} service{total !== 1 ? 's' : ''} found — page {page} of {totalPages}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {listings.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-2 rounded-lg text-sm text-ink-500 hover:bg-white hover:text-ink-700 hover:shadow-card border border-transparent hover:border-sand-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ← Prev
                </button>

                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 py-2 text-sm text-ink-300 select-none">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={[
                        'w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200',
                        p === page
                          ? 'bg-terra-400 text-white shadow-card'
                          : 'bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50 hover:text-terra-400',
                      ].join(' ')}
                    >
                      {p}
                    </button>
                  )
                )}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="px-3 py-2 rounded-lg text-sm text-ink-500 hover:bg-white hover:text-ink-700 hover:shadow-card border border-transparent hover:border-sand-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
